'use strict';

const _ = require('lodash');

const consts = require('../consts');
const utils = require('./utils');

function cancelEnvelope(cashflow, envelopeId) {
  const index = _.findIndex(cashflow.envelopes, envelope => envelope.id === envelopeId);
  _assertIndex(index, 'envelope to cancel');
  moveExpensesToVariable(cashflow, cashflow.envelopes[index]);
  cashflow.envelopes.splice(index, 1);
}

function moveExpensesToVariable(cashflow, envelope) {
  // eslint-disable-next-line no-param-reassign
  cashflow.variableTransactions = cashflow.variableTransactions.concat(envelope.actuals);
  // eslint-disable-next-line no-param-reassign
  envelope.actuals = [];
}

function updateEnvelopeAmount(cashflow, envelopeId, amount) {
  const envelope = _.find(cashflow.envelopes, envelope => envelope.id === envelopeId);
  envelope.predictedAmount = amount;
}

function updateRiseupGoalAmount(cashflow, amount) {
  const envelope = _.find(cashflow.envelopes, e => e.type === consts.RISEUP_GOAL_ENVELOPE_TYPE);
  envelope.predictedAmount = amount;
  if (envelope.details) {
    envelope.details.riseupGoalInputAmount = amount;
  }
}

function changeEnvelopeExpenseCategory(cashflow, envelopeId, expenseCategory) {
  const envelope = _.find(cashflow.envelopes, envelope => envelope.id === envelopeId);
  envelope.details.expense = expenseCategory;
}

function moveFixedToVariable(cashflow, transactionId) {
  const envelope = _findFixedEnvelopeWithTransaction(cashflow, transactionId);
  const transaction = envelope.actuals.pop();
  if (transaction.isIncome) {
    cashflow.variableIncomeTransactions.push(transaction);
  } else {
    cashflow.variableTransactions.push(transaction);
  }
  _popEnvelope(cashflow, envelope.id);
}

function _popEnvelope(cashflow, envelopeId) {
  const index = _.findIndex(cashflow.envelopes, envelope => envelope.id === envelopeId);
  _assertIndex(index, 'envelope to cancel');
  cashflow.envelopes.splice(index, 1);
}

function _findFixedEnvelopeWithTransaction(cashflow, transactionId) {
  return _.find(cashflow.envelopes, envelope => envelope.actuals.length > 0 && _.first(envelope.actuals).transactionId === transactionId);
}

function popTransactionAndContainingEnvelope(cashflow, transactionId, transactionList) {
  const fixedEnvelope = _findFixedEnvelopeWithTransaction(cashflow, transactionId);
  const transaction = popTransaction(cashflow, transactionId, transactionList);
  if (fixedEnvelope) {
    _popEnvelope(cashflow, fixedEnvelope.id);
  }
  return transaction;
}

function popTransaction(cashflow, transactionId, transactionList) {
  const transactionIndex = _.findIndex(transactionList, t => t.transactionId === transactionId);
  _assertIndex(transactionIndex, 'transaction to exclude');
  const [transaction] = transactionList.splice(transactionIndex, 1);
  return transaction;
}

function moveToFixed(cashflow, transactionId, transactionList) {
  const transaction = popTransaction(cashflow, transactionId, transactionList);
  cashflow.envelopes.push(utils.fixedEnvelopeFromTransaction(transaction));
  return transaction;
}

function showTrackingCategory(cashflow, name, amount, trackingCategoryId, icon, frequency) {
  const trackingCategoryEnvelopes = utils.createTrackingCategoryEnvelope(name, icon, amount, trackingCategoryId, cashflow.budgetDate, frequency);
  cashflow.envelopes.push(...trackingCategoryEnvelopes);
}

function moveExcludedToVariable(cashflow, transactionId, targetTransactionList) {
  const transaction = popTransactionAndContainingEnvelope(cashflow, transactionId, cashflow.excluded);
  targetTransactionList.push(transaction);
  return transaction;
}

function exclude(cashflow, transactionId, transactionList, popEnvelope) {
  const transaction = _.find(transactionList, t => t.transactionId === transactionId);
  if (popEnvelope) {
    popTransactionAndContainingEnvelope(cashflow, transactionId, transactionList);
  } else {
    popTransaction(cashflow, transactionId, transactionList);
  }

  cashflow.excluded.push(_transactionWithExcludeReason(transaction));
}

function _transactionWithExcludeReason(transaction) {
  return _.assign({}, transaction, { excludeReason: consts.EXCLUDED_REASONS.CUSTOMER_RULE });
}

function excludeVariableExpense(cashflow, transactionId) {
  exclude(cashflow, transactionId, cashflow.variableTransactions);
}

function excludeExpense(cashflow, transactionId, popEnvelope) {
  const sourceTransactions = utils.findSourceTransactionsByTransactionId(cashflow, transactionId);
  exclude(cashflow, transactionId, sourceTransactions, popEnvelope);
}

function excludeVariableIncome(cashflow, transactionId) {
  exclude(cashflow, transactionId, cashflow.variableIncomeTransactions);
}

function moveVariableExpenseToFixed(cashflow, transactionId) {
  moveToFixed(cashflow, transactionId, cashflow.variableTransactions);
}

function moveExpenseToFixed(cashflow, transactionId) {
  const sourceTransactions = utils.findSourceTransactionsByTransactionId(cashflow, transactionId);
  moveToFixed(cashflow, transactionId, sourceTransactions);
}

function moveVariableIncomeToFixed(cashflow, transactionId) {
  const transaction = moveToFixed(cashflow, transactionId, cashflow.variableIncomeTransactions);
  if (cashflow.params.variableIncomePredictionAmount) {
    // eslint-disable-next-line no-param-reassign
    cashflow.params.variableIncomePredictionAmount -= transaction.incomeAmount;
  }
}

function moveExcludedToVariableExpense(cashflow, transactionId) {
  moveExcludedToVariable(cashflow, transactionId, cashflow.variableTransactions);
}

function moveExcludedToVariableIncome(cashflow, transactionId) {
  moveExcludedToVariable(cashflow, transactionId, cashflow.variableIncomeTransactions);
}

function _isExcludedTransaction(cashflow, transactionId) {
  return _.some(cashflow.excluded, t => t.transactionId === transactionId);
}

function changeCategory(cashflow, businessName, expense, transactionId, applyTo, popEnvelope) {
  if (applyTo === consts.ENRICHMENT_INPUT_TYPES.TRANSACTION_ID) {
    _changeCategoryByTransactionId(cashflow, transactionId, expense, popEnvelope);
  } else {
    // If a category is excluded, we should only change the selected transaction categroy
    // from the excluded list, even if it has more transactions with same
    // business name. In adittion, we need to change all of the unexcluded categories that
    // belongs to the business name.
    if (_isExcludedTransaction(cashflow, transactionId)) {
      _changeCategoryByTransactionId(cashflow, transactionId, expense, popEnvelope);
    }
    _changeCategoryByBusinessName(cashflow, businessName, expense, popEnvelope);
  }
}

function _changeCategoryByTransactionId(cashflow, transactionId, expense, popEnvelope) {
  const transaction = utils.getTransactionById(cashflow, transactionId);
  _changeTransactionCategory(cashflow, transaction, expense, popEnvelope);
}

function _changeCategoryByBusinessName(cashflow, businessName, expense, popEnvelope) {
  const transactionsToChange = utils.getTransactionsByBusinessName(cashflow, businessName);
  _.forEach(transactionsToChange, transaction => {
    _changeTransactionCategory(cashflow, transaction, expense, popEnvelope);
  });
}

function _changeTransactionCategory(cashflow, transaction, expense, popEnvelope) {
  // eslint-disable-next-line no-param-reassign
  transaction[consts.EXPENSE_FIELD_NAME] = expense;
  const sourceTransactions = utils.findSourceTransactionsByTransactionId(cashflow, transaction.transactionId);
  if (popEnvelope) {
    popTransactionAndContainingEnvelope(cashflow, transaction.transactionId, sourceTransactions);
  } else {
    popTransaction(cashflow, transaction.transactionId, sourceTransactions);
  }


  if (expense === consts.ECONOMY_EXPENSE_CATEGORY) {
    const envelope = utils.findEconomyEnvelope(transaction.transactionDate, cashflow.budgetDate, cashflow.envelopes);
    if (envelope) {
      envelope.actuals.push(transaction);
    }
  } else {
    const targetTransactions = utils.findTargetTransactionsByExpenseCategory(cashflow, expense);

    targetTransactions.push(transaction);
  }
}

// this action is empty because it has no actual effect by its own on the cashflow
// it will always be applied before another action, which will do the effect - and move the transaction
// we need this action for backend action of removing the tracking category from the transaction / business name
function moveFromTracking(cashflow, matchBy, value) {}

function changeTransactionCategory(cashflow, trackingCategoryId, matchBy, value, popEnvelope) {
  if (matchBy === consts.MATCH_BY_TYPE.TRANSACTION_ID) {
    _changeTrackingCategoryByTransactionId(cashflow, value, trackingCategoryId, popEnvelope);
  } else {
    _changeTrackingCategoryByBusinessName(cashflow, value, trackingCategoryId, popEnvelope);
  }
}

function setTransactionComment(cashflow, transactionId, comment) {
  const transaction = utils.getTransactionById(cashflow, transactionId);
  transaction.customerComment = comment;
}

function splitTransaction(cashflow, transactionId, slices) {
  // TODO implement
}

function _changeTrackingCategoryByTransactionId(cashflow, transactionId, trackingCategoryId, popEnvelope) {
  const transaction = utils.getTransactionById(cashflow, transactionId);
  _changeTransactionTrackingCategory(cashflow, transaction, trackingCategoryId, popEnvelope);
}

function _changeTrackingCategoryByBusinessName(cashflow, businessName, trackingCategoryId, popEnvelope) {
  const transactionsToChange = utils.getTransactionsByBusinessName(cashflow, businessName);
  _.forEach(transactionsToChange, transaction => {
    _changeTransactionTrackingCategory(cashflow, transaction, trackingCategoryId, popEnvelope);
  });
}

function _changeTransactionTrackingCategory(cashflow, transaction, trackingCategoryId, popEnvelope) {
  const sourceTransactions = utils.findSourceTransactionsByTransactionId(cashflow, transaction.transactionId);
  if (popEnvelope) {
    popTransactionAndContainingEnvelope(cashflow, transaction.transactionId, sourceTransactions);
  } else {
    popTransaction(cashflow, transaction.transactionId, sourceTransactions);
  }
  const targetTransactions = utils.findTargetTransactionsByTrackingCategory(cashflow, trackingCategoryId);
  targetTransactions.push(transaction);
}

class NotFoundInCashflowError extends Error {
  constructor(message) {
    super(message);
  }
}

function _assertIndex(index, identifier) {
  if (index === -1) {
    throw new NotFoundInCashflowError(`Got undefined object for ${identifier}`);
  }
}

function setIncomePredictionAmount(cashflow, amount) {
  cashflow.setVariableIncomePredictionAmount(-Math.abs(amount));
}

module.exports = {
  cancelEnvelope,
  updateEnvelopeAmount,
  updateRiseupGoalAmount,
  moveVariableIncomeToFixed,
  moveExcludedToVariableExpense,
  moveExcludedToVariableIncome,
  excludeVariableIncome,
  excludeVariableExpense,
  setIncomePredictionAmount,
  changeCategory,
  changeTransactionCategory,
  moveFromTracking,
  NotFoundInCashflowError,
  changeEnvelopeExpenseCategory,
  moveVariableExpenseToFixed,
  moveFixedToVariable,
  showTrackingCategory,
  setTransactionComment,
  splitTransaction,
  excludeExpense,
  moveExpenseToFixed,
};
